import { FC } from 'react';
import clsx from 'clsx';
import { FormInstance } from 'antd';
import { FormListFieldData } from 'antd/es/form/FormList';
import CustomInput from '../../../../../../Common/CustomInput';
import styles from '../../index.module.less';
import { getSuffixDetails } from '../../../../constants';
import { EnumReportTypes, ILoanColumn, IReportRow } from '../../../../../../../types/reports';
import { IReport, IReportLoan } from '../../../../../../../hooks/reports';
import { useContextReports } from '../../../../../../../context/reports';
import { formatAmount } from '../../../../../../../utils/formatAmount';

type Props = {
  row: IReportRow;
  field: FormListFieldData;
  loan: ILoanColumn;
  loans: ILoanColumn[];
  form: FormInstance;
  isFieldDisabled?: boolean;
  handleLoansUpdate?: (event?: unknown, withLoading?: boolean) => void;
  inputsCount: number;
  disabled: boolean;
  reportType?: EnumReportTypes;
  itemKey: number;
  report?: IReport;
  isReadOnly?: boolean;
  reportLoans?: IReportLoan[];
}

const SubField:FC<Props> = ({
  row,
  field,
  loan,
  loans,
  form,
  isFieldDisabled,
  handleLoansUpdate,
  inputsCount,
  disabled,
  reportType,
  itemKey,
  report,
  isReadOnly = false,
  reportLoans,
}) => {
  const rentActive = form.getFieldValue(['tableView', 'rent', 'active']);
  const rent = form.getFieldValue(['loans', itemKey, 'externalLoan', 'rent'])

  const {
    reportLoanClosingCosts,
    reportClosingCosts,
  } = useContextReports();

  const { suffix, suffixContent } = getSuffixDetails(row.subField as IReportRow, loan, form);

  const valueDecimalPlaces = 2;
  const isExternalLoans = row?.subField?.isExternalLoans;

  const identifier = isExternalLoans ? ['loans', field.name, 'externalLoan', row?.subField?.name] :
    ['loans', field.name, row?.subField?.name, 'value']

  const name = isExternalLoans ?
    [field.name, 'externalLoan', row?.subField?.name] : [field.name, row?.subField?.name, 'value'];

  const isDisabled = row.disabled?.(loan) || disabled || isFieldDisabled ||
    (row.name === 'closingCosts' && !report?.csActive)
    || (row.name === 'downPayment' && loan?.externalLoan?.removeDownPayment)

  const tabIndex = (row?.order ?? 0) + itemKey * inputsCount + 2

  const options = !isFieldDisabled && row?.subField?.options ? row.subField.options() : undefined

  const handleChange = (value: string): void => {
    row.subField?.onChange?.(form, itemKey, value, undefined, loan, reportLoanClosingCosts, reportClosingCosts)
  }

  const handleBlur = (value: string | number | readonly string[] | undefined): void => {
    // @ts-ignore for row.subField.name
    const oldValue = isExternalLoans ? reportLoans?.[field.name]?.externalLoan?.[(row.subField as IReportRow).name]
      : reportLoans?.[field.name]?.[(row.subField as IReportRow).name];

    // const formattedOldValue = row?.subField?.prefix === '$' && oldValue ? formatAmount(parseFloat(oldValue), {
    //   max: valueDecimalPlaces
    // }) : oldValue

    if (value !== oldValue && handleLoansUpdate) {
      handleLoansUpdate({}, true);
    }
  };

  if (rentActive && rent) {
    return (
      <p style={{
        margin: 0,
        fontSize: '0.75rem',
        color: '#8d94a2',
        minHeight: '0',
        width: '100%',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis'
      }}>{row?.subField?.prefix}0{suffix}</p>
    )
  }

  return (
    <CustomInput
      suffixContent={suffixContent}
      withEclipses
      formItemProps={{
        ...field,
        className: 'w-100 table-form-item',
        name,
      }}
      tabIndex={tabIndex}
      value={row?.subField?.prepareData?.(loan, loans, form, itemKey)}
      identifier={identifier}
      prefix={row?.subField?.prefix}
      suffix={suffix}
      disabled={isDisabled}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={row?.subField?.placeholder || '0'}
      inputClassName={clsx({ [styles.bolded]: row.bolded }, 'custom-input-focused-border')}
      options={options}
      showArrow={!!row?.subField?.options}
      additionalText={row?.subField?.additionalText?.(loan, loans, reportType)}
      rowStyles={{ fontSize: '0.75rem', minHeight: 0, color: 'var(--color-gray)' }}
      isReadOnly={isReadOnly}
      valueDecimalPlaces={valueDecimalPlaces}
      informationText={row?.subField?.informationText?.(loan, form)}
    />
  );
}

export default SubField;
